import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Markdown -> HTML compatível com os nós do TipTap usados na wiki
// (headings, listas, tabelas, blockquote, código, links, hr, imagens).
marked.setOptions({
  gfm: true,
  breaks: false,
  headerIds: false,
  mangle: false,
});

const LINHA_TABELA = /^\s*\|.*\|\s*$/;

/**
 * Conteúdo colado/salvo na wiki frequentemente vem com linha em branco entre as
 * linhas da tabela — o GFM exige que fiquem contíguas, senão vira texto solto.
 * Remove as linhas em branco entre linhas de tabela consecutivas.
 */
const normalizarTabelas = (texto) => {
  const linhas = texto.split('\n');
  const saida = [];

  for (let i = 0; i < linhas.length; i++) {
    const linha = linhas[i];

    if (linha.trim() === '' && LINHA_TABELA.test(saida[saida.length - 1] || '')) {
      // olha adiante: se a próxima linha não vazia também é de tabela, descarta o vazio
      let j = i;
      while (j < linhas.length && linhas[j].trim() === '') j++;
      if (j < linhas.length && LINHA_TABELA.test(linhas[j])) {
        i = j - 1;
        continue;
      }
    }

    saida.push(linha);
  }

  return saida.join('\n');
};

export const markdownToHtml = (md) => {
  if (!md || typeof md !== 'string') return '';

  // O marcador de quebra de página vai como comentário no .md; ao voltar,
  // vira de novo o bloco de quebra (o sanitizador removeria o comentário).
  const texto = normalizarTabelas(md.replace(/\r\n/g, '\n'))
    .replace(/<!--\s*quebra-de-pagina\s*-->/gi, '<div data-quebra-pagina="true"></div>');

  const html = marked.parse(texto);

  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'rel', 'colspan', 'rowspan', 'align', 'data-quebra-pagina'],
    FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'input'],
  });
};

// Marcadores de sintaxe markdown procurados no texto.
const MARCADORES = [
  /^#{1,6}\s+\S/m,             // heading
  /^\s*>\s+\S/m,               // blockquote
  /^\s*[-*+]\s+\S/m,           // lista não ordenada
  /^\s*\d+\.\s+\S/m,           // lista ordenada
  /^\s*\|.*\|\s*$/m,           // tabela
  /^\s*```/m,                  // bloco de código cercado
  /^\s*(-{3,}|\*{3,}|_{3,})\s*$/m, // linha horizontal
  /\*\*[^*\n]+\*\*/,           // negrito
  /\[[^\]\n]+\]\([^)\s]+\)/,   // link
];

/**
 * Heurística conservadora: só considera markdown quando há pelo menos
 * `minimo` tipos distintos de marcador. Evita formatar texto comum que
 * por acaso tenha um hífen ou um "#".
 */
export const looksLikeMarkdown = (text, minimo = 2) => {
  if (!text || typeof text !== 'string') return false;
  if (text.trim().length < 12) return false;
  return MARCADORES.filter((re) => re.test(text)).length >= minimo;
};

/**
 * Extrai o texto puro de um documento TipTap (ou string).
 * Usado para recuperar markdown que foi salvo como parágrafos crus.
 */
export const docToPlainText = (content) => {
  if (!content) return '';
  if (typeof content === 'string') {
    try {
      return docToPlainText(JSON.parse(content));
    } catch {
      return content;
    }
  }

  const linhas = [];
  const percorrer = (no, raiz = false) => {
    if (!no) return;
    if (no.type === 'text') {
      linhas.push(no.text || '');
      return;
    }
    if (Array.isArray(no.content)) no.content.forEach((filho) => percorrer(filho));
    // blocos de nível raiz viram quebra de linha
    if (raiz && no.type !== 'text') linhas.push('\n');
  };

  if (Array.isArray(content.content)) content.content.forEach((no) => percorrer(no, true));
  return linhas.join('').replace(/\n{3,}/g, '\n\n').trim();
};

/**
 * Documento TipTap que é, na prática, markdown cru salvo como parágrafos
 * (páginas antigas da wiki). Só é verdadeiro quando o doc não tem nenhum
 * bloco formatado — se já houver heading/tabela/lista, o conteúdo é legítimo.
 */
export const isRawMarkdownDoc = (content) => {
  const doc = typeof content === 'string' ? (() => { try { return JSON.parse(content); } catch { return null; } })() : content;

  if (doc && Array.isArray(doc.content)) {
    const TIPOS_FORMATADOS = ['heading', 'bulletList', 'orderedList', 'taskList', 'table', 'blockquote', 'codeBlock', 'image', 'horizontalRule', 'htmlBlock', 'youtube'];
    const temFormatacao = doc.content.some((no) => TIPOS_FORMATADOS.includes(no.type));
    if (temFormatacao) return false;
  }

  return looksLikeMarkdown(docToPlainText(content), 3);
};
